/**
 * Die vier Zustände einer datengetriebenen Ansicht.
 *
 * Jede Ansicht, die Daten lädt, kennt vier Lagen: sie lädt, sie ist leer, sie
 * ist auf einen Fehler gelaufen, oder sie zeigt Inhalt. Bisher formulierte
 * jede Ansicht die ersten drei selbst — mit dem Ergebnis, dass sie in vielen
 * gar nicht vorkamen. Eine leere Liste ohne Text liest sich wie ein
 * Ladefehler, und ein Fehler ohne Wiederholung ist eine Sackgasse.
 *
 * Die Bausteine hier sind bewusst schmal: sie setzen keine Karte drumherum
 * und keine Überschrift. Die Ansicht entscheidet, wo sie stehen.
 */

import type { ReactNode } from 'react';
import { stil } from './stil';
import { IC } from '../entwurf/katalog';

/* ------------------------------------------------------------------ Laden */

/**
 * Platzhalter in der Form des künftigen Inhalts.
 *
 * Kein kreisender Ring: ein Platzhalter, der die spätere Gestalt andeutet,
 * lässt die Seite ruhiger wirken und verhindert das Springen des Layouts,
 * wenn die Daten eintreffen.
 */
export function Laedt({ zeilen = 6, hoehe = 40 }: { zeilen?: number; hoehe?: number }) {
  return (
    <div role="status" aria-label="Wird geladen"
         style={stil('display:flex;flex-direction:column;gap:8px;padding:16px')}>
      {Array.from({ length: zeilen }, (_, i) => (
        <div key={i} className="platzhalter"
             style={{ height: hoehe, opacity: Math.max(0.35, 1 - i * 0.12) }} />
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------- Leer */

export interface LeerProps {
  /** Was fehlt — in einem Satz, ohne Ausrufezeichen. */
  text: string;
  /** Warum es fehlt oder was zu tun wäre. Optional. */
  hinweis?: string;
  /** Schaltfläche, die aus der Lage herausführt. */
  aktion?: ReactNode;
  /* Nur ein Name, den es gibt. `| string` stand hier und machte die
     Typangabe wirkungslos — die Vereinigung mit `string` ist wieder
     `string`. */
  icon?: keyof typeof IC;
}

export function Leer({ text, hinweis, aktion, icon = 'box' }: LeerProps) {
  return (
    <div className="leer">
      <svg aria-hidden="true" width="34" height="34" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
           style={stil('color:var(--text-tertiary)')}>
        <path d={IC[icon]} />
      </svg>
      <p style={stil('margin:0;font-size:15px;color:var(--text-primary);max-inline-size:46ch')}>
        {text}
      </p>
      {hinweis && (
        <p style={stil('margin:0;font-size:13px;max-inline-size:52ch;text-wrap:pretty')}>
          {hinweis}
        </p>
      )}
      {aktion}
    </div>
  );
}

/* ------------------------------------------------------------------ Fehler */

/**
 * Der erste Fehler aus mehreren Abrufen.
 *
 * Die meisten Ansichten lesen aus drei bis fünf Sammlungen. Fällt eine davon
 * aus, standen sie bisher einfach leer da — die Lizenzansicht meldete dann
 * „0 €" Gesamtkosten, die Organisationsübersicht „Keine Organisationseinheit
 * vorhanden". Beides ist eine Aussage über den Bestand, und beide waren
 * falsch: es fehlten nicht die Daten, es fehlte die Antwort.
 *
 * Gemeldet wird der erste Ausfall, nicht alle. Wer drei Meldungen übereinander
 * bekommt, liest keine davon.
 */
export function ersterFehler(
  ...abrufe: Array<{ fehler: string | null; erneut: () => void }>
): { text: string; erneut: () => void } | null {
  const treffer = abrufe.find((a) => a.fehler !== null);
  return treffer ? { text: treffer.fehler as string, erneut: treffer.erneut } : null;
}

export interface FehlerProps {
  /** Meldung der Schnittstelle. */
  text: string;
  /** Erneut versuchen — ohne sie ist ein Fehler eine Sackgasse. */
  erneut?: (() => void) | undefined;
}

export function Fehler({ text, erneut }: FehlerProps) {
  return (
    <div className="fehlerfeld" role="alert">
      <svg aria-hidden="true" width="34" height="34" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l9.5 17H2.5L12 3zM12 9v5M12 17.2v.1" />
      </svg>
      <p style={stil('margin:0;font-size:15px')}>Die Daten konnten nicht geladen werden.</p>
      <p style={stil('margin:0;font-size:13px;color:var(--text-secondary);max-inline-size:52ch')}>
        {text}
      </p>
      {erneut && (
        <button type="button" className="knopf knopf--rand" onClick={erneut}>
          Erneut versuchen
        </button>
      )}
    </div>
  );
}
